import type { ToolCallDto } from '@paladin-monorepo/common';

export interface ToolCallDisplayInfo {
  nameKey: string;
  contextKey?: string;
  contextValues?: Record<string, string>;
}

const SKIP_CONTEXT_KEYS = new Set(['versioningChoice', 'locale']);

/** Internal tools that should not appear in the chat tool-call summary */
export const HIDDEN_LLM_CHAT_TOOL_NAMES = new Set([
  'request_inquiry_versioning_choice',
  'ask_user_question',
]);

export function getVisibleToolCalls(toolCalls: ToolCallDto[]): ToolCallDto[] {
  return toolCalls.filter((tc) => !HIDDEN_LLM_CHAT_TOOL_NAMES.has(tc.name));
}

const INQUIRY_FIELD_KEYS = [
  'title',
  'objectives',
  'specification',
  'categoryId',
  'industryIds',
  'deliverableIds',
  'budgetMin',
  'budgetMax',
  'budgetCurrency',
  'currency',
  'startDate',
  'endDate',
  'deadline',
  'teamRequirement',
  'expertiseIds',
  'optionalReqs',
  'ndaRequired',
  'internalNotes',
  'projectCode',
  'kpis',
] as const;

const OFFER_FIELD_KEYS = [
  'proposal',
  'outOfScope',
  'caseStudyIds',
  'lineItems',
  'milestones',
  'team',
  'risks',
  'sla',
  'validityDate',
] as const;

function formatStringList(values: unknown[]): string {
  return values
    .map((value) => {
      if (typeof value === 'string') return value;
      if (typeof value === 'number' || typeof value === 'boolean') return String(value);
      return null;
    })
    .filter((value): value is string => !!value?.trim())
    .join(', ');
}

function formatKpiDescriptions(kpis: unknown[]): string {
  return kpis
    .map((entry) => {
      if (!entry || typeof entry !== 'object') return null;
      const description = 'description' in entry ? String(entry.description ?? '').trim() : '';
      return description || null;
    })
    .filter((value): value is string => !!value)
    .join(', ');
}

function getUpdatedFieldKeys(args: Record<string, unknown>, allowedKeys: readonly string[]): string[] {
  return allowedKeys.filter((key) => {
    if (SKIP_CONTEXT_KEYS.has(key)) return false;
    const value = args[key];
    if (value === undefined || value === null) return false;
    if (typeof value === 'string') return value.trim().length > 0;
    if (Array.isArray(value)) return value.length > 0;
    return true;
  });
}

function getToolCallContext(
  name: string,
  args: Record<string, unknown>,
): Pick<ToolCallDisplayInfo, 'contextKey' | 'contextValues'> {
  switch (name) {
    case 'add_expected_deliverables':
    case 'add_industries':
    case 'add_expertise': {
      const items = Array.isArray(args.items) ? formatStringList(args.items) : '';
      return items
        ? { contextKey: 'llmChat.toolCalls.context.items', contextValues: { items } }
        : {};
    }
    case 'add_kpis': {
      const items = Array.isArray(args.kpis) ? formatKpiDescriptions(args.kpis) : '';
      return items
        ? { contextKey: 'llmChat.toolCalls.context.items', contextValues: { items } }
        : {};
    }
    case 'update_inquiry': {
      const fields = getUpdatedFieldKeys(args, INQUIRY_FIELD_KEYS);
      if (fields.length === 0) return {};
      return {
        contextKey: 'llmChat.toolCalls.context.updatedFields',
        contextValues: {
          fields: fields.map((field) => `llmChat.toolCalls.fields.${field}`).join('|'),
        },
      };
    }
    case 'update_offer': {
      const fields = getUpdatedFieldKeys(args, OFFER_FIELD_KEYS);
      if (fields.length === 0) return {};
      return {
        contextKey: 'llmChat.toolCalls.context.updatedFields',
        contextValues: {
          fields: fields.map((field) => `llmChat.toolCalls.fields.${field}`).join('|'),
        },
      };
    }
    case 'lookup_categories':
    case 'lookup_industries':
    case 'lookup_deliverables':
    case 'lookup_expertise': {
      const search = typeof args.search === 'string' ? args.search.trim() : '';
      return search
        ? { contextKey: 'llmChat.toolCalls.context.search', contextValues: { query: search } }
        : {};
    }
    default:
      return {};
  }
}

export function getToolCallDisplayInfo(toolCall: ToolCallDto): ToolCallDisplayInfo {
  const args = toolCall.arguments ?? {};
  return {
    nameKey: `llmChat.toolCalls.names.${toolCall.name}`,
    ...getToolCallContext(toolCall.name, args),
  };
}

export function formatUpdatedFields(fields: string, translate: (key: string) => string): string {
  return fields
    .split('|')
    .map((fieldKey) => translate(fieldKey))
    .join(', ');
}
